/**
 * Temp-password modal (spec 0006 §5.7) — refreshed presentation.
 *
 * Reused by:
 *   * the invite flow (POST /admin-users/ → 201)
 *   * the rotate-password flow (POST /admin-users/{id}/rotate-password/ → 200)
 *
 * Non-dismissable (``role="alertdialog"``) until the operator ticks the
 * "I have shared this temp password" checkbox. The password lives only
 * in component state — never in localStorage, never in the URL, never
 * in an axios payload after the create call.
 *
 * Email-status banner reflects spec §5.7:
 *
 *   * email_dispatched=true: success banner ("we sent it; verify it arrived")
 *   * email_dispatched=false AND send_email_requested=true: warning banner
 *     ("email failed/disabled; share out-of-band")
 *   * send_email_requested=false: no banner.
 */
import { useState } from "react";
import { AlertCircle, Check, CheckCircle2, Copy, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";

export interface TempPasswordModalProps {
  title: string;
  /** ``true`` when invite/rotate flow surfaces ``email_dispatched=true``. */
  emailDispatched: boolean;
  /** Did the caller *ask* for email dispatch? Used to pick the warning-vs-success banner. */
  sendEmailRequested: boolean;
  /** Target operator's email — used in the banner copy + screen-reader label. */
  targetEmail: string;
  /** The temp password to display. Never persisted by this component. */
  tempPassword: string;
  /** Called when the operator ticks the checkbox and hits Done. */
  onDone: () => void;
}

export function TempPasswordModal({
  title,
  emailDispatched,
  sendEmailRequested,
  targetEmail,
  tempPassword,
  onDone,
}: TempPasswordModalProps) {
  const [acknowledged, setAcknowledged] = useState(false);
  const [copied, setCopied] = useState(false);

  async function copyToClipboard() {
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(tempPassword);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      } catch {
        // Silent — operator can still read + retype the password.
      }
    }
  }

  // Non-dismissable: no overlay click handler, no Escape close. The
  // ``alertdialog`` role signals that to ATs.
  return (
    <div
      role="alertdialog"
      aria-labelledby="temp-password-modal-title"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
    >
      <div className="w-full max-w-lg rounded-lg border bg-background p-6 shadow-xl">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <KeyRound className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 id="temp-password-modal-title" className="text-lg font-semibold">
              {title}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Share this temporary password with{" "}
              <span className="font-medium text-foreground">{targetEmail}</span>{" "}
              securely. We will not show it again.
            </p>
          </div>
        </div>

        {sendEmailRequested ? (
          emailDispatched ? (
            <div className="mt-4 flex items-start gap-2 rounded-md border border-success/40 bg-success/5 p-3 text-sm text-success">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
              <p>
                An invite email is being sent to{" "}
                <span className="font-medium">{targetEmail}</span>. Verify they
                receive it.
              </p>
            </div>
          ) : (
            <div className="mt-4 flex items-start gap-2 rounded-md border border-warning/40 bg-warning/5 p-3 text-sm text-warning">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>
                Email delivery failed or is disabled. Share the password
                out-of-band.
              </p>
            </div>
          )
        ) : null}

        <div className="mt-4">
          <label
            htmlFor="temp-password-code"
            className="text-xs font-semibold uppercase tracking-wider text-muted-foreground"
          >
            Temporary password
          </label>
          <div className="mt-1.5 flex items-stretch gap-2">
            <code
              id="temp-password-code"
              data-testid="temp-password"
              className="flex-1 select-all break-all rounded-md border bg-muted/40 p-3 font-mono text-sm"
            >
              {tempPassword}
            </code>
            <Button
              type="button"
              variant="outline"
              onClick={copyToClipboard}
              aria-label="Copy temporary password to clipboard"
            >
              {copied ? <Check /> : <Copy />}
              {copied ? "Copied" : "Copy"}
            </Button>
          </div>
        </div>

        <p className="mt-4 text-xs text-muted-foreground">
          The operator must change this password on first login and enroll a
          TOTP device.
        </p>

        <label className="mt-6 flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-input text-primary focus-visible:ring-2 focus-visible:ring-ring"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            data-testid="temp-password-ack"
          />
          <span>
            I have shared this temporary password with the operator (via email
            or out-of-band).
          </span>
        </label>

        <div className="mt-6 flex justify-end">
          <Button
            type="button"
            disabled={!acknowledged}
            onClick={onDone}
            data-testid="temp-password-done"
          >
            Done
          </Button>
        </div>
      </div>
    </div>
  );
}

export default TempPasswordModal;
